import { computed, nextTick, ref, watch, type InjectionKey } from 'vue'
import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
import { storeToRefs } from 'pinia'
import type { SalarieDTO } from '@/features/api/extranet/dto/Demande/AF'
import type { AdresseDTO } from '@/features/api/extranet/dto/shared'
import { deepEqual, isNullOrUndefined } from '@/features/shared/tools'
import { useEventBus } from '@/features/application/app-events/AppEvents'
import type { SalarieRechercheDTO } from '@/features/api/extranet/dto/Demande/shared'
import { NOTIF_SAISIE_SALARIE_MODIFIÉE } from '@/features/interface/demande/shared/language'

export const SalarieActionKey = Symbol() as InjectionKey<string>
export type SalarieAction = ReturnType<typeof useSalarieActions>

export function useSalarieActions() {
  const store = useDemandeAFStore()
  const eventBus = useEventBus()

  const { model } = storeToRefs(store)
  const { setupCustomUpdater } = store

  const idScrollDownToSaisie = 'salariés-scroll'

  async function scrollDownToSaisie() {
    await nextTick()
    await nextTick()
    document.getElementById(idScrollDownToSaisie)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const activeSalariéIndex = ref<number | null>(null)

  const listeSalariés = computed<SalarieDTO[]>(() => {
    return model.value?.salaries || []
  })

  const salariéEnCours = computed<SalarieDTO | undefined>(() => {
    if (activeSalariéIndex.value === null) return
    return listeSalariés.value![activeSalariéIndex.value]
  })

  const nbMaxSalariésAtteint = computed(() => listeSalariés.value.length >= 30)

  const metaSalarieEnCours = computed(() => {
    if (typeof activeSalariéIndex.value !== 'number') return null
    return store.meta.salaries?.at(activeSalariéIndex.value)
  })

  const creerSalariéVide = (): SalarieDTO => {
    return {
      nomNaissance: null,
      nom: null,
      prenom: null,
      dateNaissance: null,
      sexe: null,
      nirTemporaire: null,
      nir: null,
      beneficieReconnaissanceTravailleurHandicape: null,
      adresse: {} as AdresseDTO,
      email: null,
      telephone: null,
      natureContrat: null,
      statut: null,
      niveauFormation: null,
      numeroDossierCpf: null,
      tauxHoraireBrut: null,
      pctTauxCharge: null,
      source: 'Portail',
      csp: null,
      signature: null,
    }
  }

  const patchAjouterSalarié = setupCustomUpdater<void>((patch) => {
    if (!patch.salaries) patch.salaries = []
    patch.salaries?.push(creerSalariéVide())
  })

  async function ajouterSalarié() {
    await patchAjouterSalarié()
    activeSalariéIndex.value = model.value!.salaries!.length - 1
    définirSalariéEnCours(activeSalariéIndex.value)
  }

  const patchRemplacerSalarié = setupCustomUpdater<SalarieRechercheDTO>((patch, salarieRecherché) => {
    const salarie = {
      ...creerSalariéVide(),
      ...salarieRecherché,
    }
    patch.salaries![activeSalariéIndex.value!] = salarie
  })

  function définirSalariéEnCours(index: number | null) {
    const isTogglingOff = index === activeSalariéIndex.value
    activeSalariéIndex.value = isTogglingOff ? null : index
    if (activeSalariéIndex.value !== null) {
      scrollDownToSaisie()
    }
  }

  const supprimerSalarié = setupCustomUpdater<number>((patch, index) => {
    const element = patch.salaries?.splice(index, 1)
    if (!element) throw 'Index inexistant'
    activeSalariéIndex.value = null
  })

  // PRT-589 - modifie origine Nir
  const patchSourceSalariéToPortail = setupCustomUpdater<{ index: number; resetNir: boolean }>((patch, payload) => {
    const salarie = patch.salaries?.at(payload.index)
    if (!salarie) return
    salarie.source = 'Portail'
    salarie.signature = null
    if (payload.resetNir) {
      salarie.nir = null
      /* Notifie utilisateur */
      eventBus?.dispatch(NOTIF_SAISIE_SALARIE_MODIFIÉE)
    }
  })

  watch(
    () => ({
      index: activeSalariéIndex.value,
      nir: salariéEnCours.value?.nir,
      signature: salariéEnCours.value?.signature,
    }),
    (newValue, oldValue) => {
      if (isNullOrUndefined(newValue.index)) return
      if (newValue.index !== oldValue.index) return
      if (salariéEnCours.value?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        index: newValue.index!,
        resetNir: false,
      })
    },
  )

  watch(
    () => ({
      index: activeSalariéIndex.value,
      nomNaissance: salariéEnCours.value?.nomNaissance,
      prenom: salariéEnCours.value?.prenom,
      sexe: salariéEnCours.value?.sexe,
      dateNaissance: salariéEnCours.value?.dateNaissance,
      nirTemporaire: salariéEnCours.value?.nirTemporaire,
      signature: salariéEnCours.value?.signature,
    }),
    (newValue, oldValue) => {
      if (isNullOrUndefined(newValue.index)) return
      if (newValue.index !== oldValue.index) return
      if (salariéEnCours.value?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        index: newValue.index!,
        resetNir: true,
      })
    },
  )

  return {
    activeSalariéIndex,
    ajouterSalarié,
    patchRemplacerSalarié,
    définirSalariéEnCours,
    supprimerSalarié,
    patchAjouterSalarié,
    scrollDownToSaisie,
    idScrollDownToSaisie,
    listeSalariés,
    salariéEnCours,
    metaSalarieEnCours,
    nbMaxSalariésAtteint,
  }
}
